import { writable } from "svelte/store"

export type Camera = {
  type: "camera"
  facing: "front" | "back"
  stream?: MediaStream
  canSwitch?: boolean
}

export type Upload = {
  type: "upload"
  file: File
}

type Files = {
  type: "files"
  files: File[]
}

/** An image source used for the image processing and capture */
export const source = {
  ...writable<Camera | Upload | Files | null>(null),

  files(files: Files["files"]) {
    this.set({ type: "files", files })
  },

  upload(file: Upload["file"]) {
    this.set({ type: "upload", file })
  },

  camera(init: Omit<Camera, "type">) {
    this.set({ type: "camera", ...init })
  },

  reset() {
    this.update((source) => {
      if (isCamera(source)) return { ...source, stream: undefined }
      if (isFiles(source)) return { ...source, files: [] }
      return source
    })
  },

  clear() {
    this.set(null)
  },
}

export const getCameraFacing = (sources: string[]) => {
  const frontCameraIdx = sources.findIndex((s) => s === "front_camera" || s === "camera")
  const backCameraIdx = sources.findIndex((s) => s === "back_camera")

  if (frontCameraIdx !== -1 && backCameraIdx !== -1)
    return frontCameraIdx < backCameraIdx ? "front" : "back"

  if (backCameraIdx !== -1) return "back"

  return "front" // default
}

export const isCamera = (source: unknown): source is Camera => (source as Camera)?.type === "camera"
export const isUpload = (source: unknown): source is Upload => (source as Upload)?.type === "upload"
export const isFiles = (source: unknown): source is Files => (source as Files)?.type === "files"

export const isIdleCamera = (source: unknown): source is Camera & { stream: undefined } =>
  isCamera(source) && (!source.stream || !source.stream.active)
